import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const Login: React.FC = () => {
  const { isDarkMode } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [schoolCode, setSchoolCode] = useState('');
  const [role, setRole] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotSchool, setShowForgotSchool] = useState(false);
  const [forgotSchoolName, setForgotSchoolName] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password, schoolCode);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSchoolCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    setForgotMessage('');
    setForgotLoading(true);

    try {
      const response = await axios.post(`${API_URL}/tenants/find-by-name`, {
        schoolName: forgotSchoolName
      });

      if (response.data.schools && response.data.schools.length > 0) {
        const schoolInfo = response.data.schools.map((s: any) => 
          `${s.schoolName} - Code: ${s.schoolCode}`
        ).join('\n');
        setForgotMessage(`Found school(s):\n${schoolInfo}`);
      } else {
        setForgotError('No schools found with this name.');
      }
    } catch (err: any) {
      setForgotError(err.response?.data?.message || 'Failed to retrieve school code. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${isDarkMode ? 'bg-gradient-to-br from-gray-800 to-gray-900' : 'bg-gradient-to-br from-blue-500 to-purple-600'}`}>
      <div className={`rounded-lg shadow-2xl p-8 max-w-md w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="text-center mb-8">
          <h1 className={`text-4xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>School Portal</h1>
          <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Sign in to your account</p>
        </div>

        {error && (
          <div className={`border px-4 py-3 rounded mb-4 ${isDarkMode ? 'bg-red-900/30 border-red-500 text-red-300' : 'bg-red-100 border-red-400 text-red-700'}`}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={`block text-sm font-bold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`} htmlFor="schoolCode">
              School Code
            </label>
            <input
              id="schoolCode"
              type="text"
              value={schoolCode}
              onChange={(e) => setSchoolCode(e.target.value.toUpperCase())}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
              placeholder="Enter school code (e.g., DEMO2024)"
              required
            />
            <button
              type="button"
              onClick={() => setShowForgotSchool(true)}
              className={`text-sm mt-1 ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
            >
              Forgot school code?
            </button>
          </div>

          <div>
            <label className={`block text-sm font-bold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`} htmlFor="role">
              Login As
            </label>
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              required
            >
              <option value="">Select your role</option>
              <option value="admin">Administrator</option>
              <option value="teacher">Teacher</option>
              <option value="student">Student</option>
              <option value="parent">Parent</option>
            </select>
          </div>

          <div>
            <label className={`block text-sm font-bold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`} htmlFor="email">
              Email or Username
            </label>
            <input
              id="email"
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
              placeholder="Enter your email or username"
              required
            />
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Students can use admission number or roll number
            </p>
          </div>

          <div>
            <label className={`block text-sm font-bold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`} htmlFor="password">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
              placeholder="Enter your password"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-2 px-4 rounded-lg transition duration-200 disabled:opacity-50 ${isDarkMode ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Don't have a school account?{' '}
            <a href="/signup" className={`font-semibold ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}>
              Register your school
            </a>
          </p>
        </div>
      </div>

      {/* Forgot School Code Modal */}
      {showForgotSchool && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className={`rounded-lg shadow-2xl p-8 max-w-md w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex justify-between items-center mb-6">
              <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Forgot School Code?</h2>
              <button
                onClick={() => {
                  setShowForgotSchool(false);
                  setForgotSchoolName('');
                  setForgotMessage('');
                  setForgotError('');
                }}
                className={isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Enter your school name to retrieve your school code
            </p>

            {forgotError && (
              <div className={`border px-4 py-3 rounded mb-4 ${isDarkMode ? 'bg-red-900/30 border-red-500 text-red-300' : 'bg-red-100 border-red-400 text-red-700'}`}>
                {forgotError}
              </div>
            )}

            {forgotMessage && (
              <div className={`border px-4 py-3 rounded mb-4 whitespace-pre-line ${isDarkMode ? 'bg-green-900/30 border-green-500 text-green-300' : 'bg-green-100 border-green-400 text-green-700'}`}>
                {forgotMessage}
              </div>
            )}

            <form onSubmit={handleForgotSchoolCode} className="space-y-4">
              <div>
                <label className={`block text-sm font-bold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`} htmlFor="forgotSchoolName">
                  School Name
                </label>
                <input
                  id="forgotSchoolName"
                  type="text"
                  value={forgotSchoolName}
                  onChange={(e) => setForgotSchoolName(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                  placeholder="Enter your school name"
                  required
                />
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Enter partial name for search (e.g., "Demo School")
                </p>
              </div>

              <button
                type="submit"
                disabled={forgotLoading}
                className={`w-full py-2 px-4 rounded-lg transition duration-200 disabled:opacity-50 ${isDarkMode ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
              >
                {forgotLoading ? 'Searching...' : 'Retrieve School Code'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
